import { css } from '@emotion/react';
import { superstructResolver } from '@hookform/resolvers/superstruct';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import IconAddFilled from '~icons/carbon/add-filled';

import {
	useCancelScheduledGoogleMyBusinessTemplateMutation,
	useDeleteGoogleMyBusinessTemplatesMutation,
	useGMBTemplateNameUniquenessMutation,
	useScheduleGoogleMyBusinessTemplateMutation,
	useUploadGoogleMyBusinessTemplateMutation,
} from '@adflow/frontend/feat-google-my-business';
import {
	ButtonIconDelete,
	ButtonRow,
	ButtonSave,
	ButtonWithLoading,
} from '@adflow/frontend/ui-buttons';
import {
	HubTooltip,
	LineClamp,
	RenderSimpleImage,
} from '@adflow/frontend/ui-common';
import {
	convertItemsToLookUp,
	FormControl,
	InputTextFieldLengthWithControl,
	InputTextFieldWithControl,
	SelectorDateTime,
	SelectorDateTimeWithControl,
	SelectorSelectWithControl,
	SelectorSelectWithLookupAndControl,
	TextareaWithControl,
} from '@adflow/frontend/ui-form';
import { ScrollShadowBox } from '@adflow/frontend/ui-layout';
import { ModalDialog, WarnOnNavAway } from '@adflow/frontend/ui-modal';
import {
	REQUIRED_MARK,
	su,
	theme,
	typography,
} from '@adflow/frontend/ui-theme';
import { useAbility } from '@adflow/frontend/util-auth';
import {
	CollapseSectionProvider,
	useRect,
	useWindowViewWidth,
} from '@adflow/frontend/util-common';
import { LinkButton, useDispatchNavAction } from '@adflow/frontend/util-router';
import {
	AddGMBTemplateDto,
	GMB_STANDARD_CTA_TYPE_CTA_URL_NOT_NEEDED,
} from '@adflow/shared/data-google-my-business';
import { formatDateAndTime } from '@adflow/shared/util-formatting';
import type { ViewGMBTemplateDtoWithUploadResult } from '@adflow/shared/util-history';

import {
	parseTemplateSection,
	type TemplateSection,
} from '../../Facebook/FacebookTemplates/FacebookHeaderBar';
import { FacebookHeaderBar } from '../../Facebook/FacebookTemplates/FacebookHeaderBar';
import { sharedStyles } from '../../Facebook/FacebookTemplates/sharedStyles';
import { TemplateMediaModal } from '../../GoogleAds/GoogleAdsTemplates/TemplateMediaModal';

import { GMBDuplicateTemplate } from './GMBDuplicateTemplate';
import { GoogleAdsLocationSelector } from './GMBLocationsSelector';
import { GMBTemplateHistory } from './GMBTemplateHistory';
import { goToGMBTemplatesList } from './GMBTemplateNavActions';

const styles = {
	formContainer: css({
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
		height: '100%',
		maxHeight: '100%',
		maxWidth: '1920px',
		backgroundColor: theme.colors.background.form,
	}),
	title: css({
		marginTop: '1.25rem',
		marginBottom: '1.25rem',
		paddingLeft: '1.5rem',
		paddingRight: '1.5rem',
	}),
	...sharedStyles,
};

interface GMBTemplateFormViewProps {
	title: string;
	template: Omit<ViewGMBTemplateDtoWithUploadResult, 'createdById'>;
	onSubmit: (values: AddGMBTemplateDto) => void;
}

export function GMBTemplateFormView({
	template,
	onSubmit,
}: GMBTemplateFormViewProps) {
	const { t } = useTranslation(['common', 'facebook', 'google-ads']);

	const containerRef = useRef<HTMLFormElement>(null);
	const containerRect = useRect(containerRef);
	const pageWidth = useWindowViewWidth();

	const navigate = useDispatchNavAction();

	const { mutateAsync: validateNames, isPending: nameValidationPending } =
		useGMBTemplateNameUniquenessMutation();
	const { mutateAsync: deleteTemplate, isPending: deletePending } =
		useDeleteGoogleMyBusinessTemplatesMutation();
	const { mutateAsync: uploadTemplate, isPending: uploadPending } =
		useUploadGoogleMyBusinessTemplateMutation();
	const { mutateAsync: scheduleTemplate, isPending: schedulePending } =
		useScheduleGoogleMyBusinessTemplateMutation();
	const {
		mutateAsync: cancelScheduledTemplate,
		isPending: cancelSchedulePending,
	} = useCancelScheduledGoogleMyBusinessTemplateMutation();

	const [confirmDelete, setConfirmDelete] = useState(false);
	const [showMedia, setShowMedia] = useState(false);
	const [scheduledPublishAt, setScheduledPublishAt] = useState(
		template.scheduledPublishAt ?? ''
	);

	const updateOrCreate = template.id === -1 ? 'create' : 'update';

	useEffect(() => {
		setScheduledPublishAt(template.scheduledPublishAt ?? '');
	}, [template.scheduledPublishAt]);

	const { ability, missingPermissionText } = useAbility();

	const canEditGMBTemplates = useMemo(
		() => ability.can(updateOrCreate, 'GMB/Templates'),
		[ability, updateOrCreate]
	);

	const editMissingPermissionText = useMemo(
		() =>
			canEditGMBTemplates
				? ''
				: missingPermissionText(updateOrCreate, 'GMB/Templates'),
		[missingPermissionText, updateOrCreate, canEditGMBTemplates]
	);

	const canDeleteGMBTemplates = useMemo(
		() => ability.can('archive', 'GMB/Templates'),
		[ability]
	);

	const deleteMissingPermissionText = useMemo(
		() =>
			canDeleteGMBTemplates
				? ''
				: missingPermissionText('archive', 'GMB/Templates'),
		[missingPermissionText, canDeleteGMBTemplates]
	);

	const canUploadGMBTemplates = useMemo(
		() => ability.can('upload', 'GMB/Templates'),
		[ability]
	);

	const uploadMissingPermissionText = useMemo(
		() =>
			canUploadGMBTemplates
				? ''
				: missingPermissionText('upload', 'GMB/Templates'),
		[missingPermissionText, canUploadGMBTemplates]
	);

	const languageOptions = useMemo(
		() =>
			convertItemsToLookUp(['en-US', 'fr-FR'], (i) =>
				t(`common:language-${i}`)
			),
		[t]
	);

	const [searchParams, setSearchParams] = useSearchParams();
	const section = parseTemplateSection(searchParams.get('section'));

	const setSection = useCallback(
		(newSection: TemplateSection) => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					if (newSection === 'template') {
						next.delete('section');
					} else {
						next.set('section', newSection);
					}
					return next;
				},
				{ replace: true }
			);
		},
		[setSearchParams]
	);

	const handleUpload = async () => {
		await uploadTemplate({ templateId: template.id });
	};

	const handleSchedule = async () => {
		if (!scheduledPublishAt) {
			return;
		}

		await scheduleTemplate({
			templateId: template.id,
			scheduledPublishAt,
		});
	};

	const handleCancelSchedule = async () => {
		await cancelScheduledTemplate({ templateId: template.id });
	};

	const uploadErrorsCount = useMemo(() => {
		if (!template.uploadResult?.newData?.results) {
			return 0;
		}

		let errorsCount = 0;
		for (const result of template.uploadResult.newData.results) {
			if (result.status !== 'SUCCESS') {
				errorsCount += 1;
			}
		}

		return errorsCount;
	}, [template.uploadResult?.newData?.results]);

	const scheduledPublishDateTime = useMemo(
		() => (scheduledPublishAt ? DateTime.fromISO(scheduledPublishAt) : null),
		[scheduledPublishAt]
	);

	const hasScheduledPublish = Boolean(template.scheduledPublishAt);
	const isScheduledPublishInPast = useMemo(() => {
		if (!scheduledPublishAt) {
			return false;
		}

		const scheduledDate = new Date(scheduledPublishAt);
		return (
			!Number.isNaN(scheduledDate.getTime()) &&
			scheduledDate.getTime() <= Date.now()
		);
	}, [scheduledPublishAt]);

	const formMethods = useForm({
		resolver: superstructResolver(AddGMBTemplateDto, {
			coerce: true,
			mask: true,
		}),
		defaultValues: template as AddGMBTemplateDto,
	});

	const mediaFile = useWatch({
		control: formMethods.control,
		name: 'image',
	});

	const postType = useWatch({
		control: formMethods.control,
		name: 'postType',
	});

	const ctaType = useWatch({
		control: formMethods.control,
		name: 'ctaType',
	});

	const watchedLocations = useWatch({
		control: formMethods.control,
		name: 'locations',
	});

	return (
		<FormProvider {...formMethods}>
			<CollapseSectionProvider>
				<form
					ref={containerRef}
					css={styles.formContainer}
					onSubmit={formMethods.handleSubmit(async (values) => {
						const errors = await validateNames(values);

						if (errors.validateErrors.length > 0) {
							for (const error of errors.validateErrors) {
								// eslint-disable-next-line @typescript-eslint/no-explicit-any
								const fieldName = error.field as any;
								formMethods.setError(fieldName, {
									type: error.type,
									message: error.message,
								});
							}
							return;
						}

						onSubmit(values);
					})}
				>
					<FacebookHeaderBar
						section={section}
						setSection={setSection}
						uploadErrorsCount={uploadErrorsCount}
						sectionList={['template', 'history']}
					/>
					<div
						css={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
						}}
					>
						<div css={styles.title}>
							<span css={[typography.title, su.text2xl, { fontWeight: 700 }]}>
								{t('facebook:tabTemplate')}:{' '}
							</span>
							<LineClamp
								css={[
									typography.title,
									su.text2xl,
									{
										fontWeight: 400,
										maxWidth: `${Math.max(0, pageWidth - (containerRect?.left ?? 0) - 200)}px`,
									},
								]}
								lines={2}
							>
								{formMethods.getValues('name')}
							</LineClamp>
						</div>
						{template.id === -1 ? null : (
							<div
								css={{
									display: 'flex',
									alignItems: 'center',
									gap: '0.75rem',
									marginRight: '1.5rem',
									minWidth: '530px',
								}}
							>
								<div css={{ minWidth: '18rem' }}>
									<FormControl
										label={t('google-ads:schedulePublish')}
										css={{
											...typography.regular,
											marginBottom: '0.25rem',
										}}
									>
										<SelectorDateTime
											value={scheduledPublishDateTime}
											onChange={(value, context) => {
												if (value === null) {
													setScheduledPublishAt('');
													return;
												}

												if (!context.validationError) {
													setScheduledPublishAt(value.toISO() ?? '');
												}
											}}
											disablePast
											clearable
											readOnly={!canUploadGMBTemplates}
										/>
									</FormControl>
								</div>
								<HubTooltip
									title={
										canUploadGMBTemplates
											? watchedLocations?.length
												? scheduledPublishAt
													? isScheduledPublishInPast
														? t('google-ads:schedulePublishMustBeFuture')
														: ''
													: t('google-ads:selectPublishDateAndTime')
												: t('uploadNoLocations')
											: uploadMissingPermissionText
									}
								>
									<ButtonWithLoading
										onClick={handleSchedule}
										loading={schedulePending}
										color="primary"
										variant="contained"
										disabled={
											!canUploadGMBTemplates ||
											!watchedLocations?.length ||
											!scheduledPublishAt ||
											isScheduledPublishInPast
										}
									>
										{hasScheduledPublish
											? t('google-ads:reschedule')
											: t('google-ads:schedule')}
									</ButtonWithLoading>
								</HubTooltip>
								{hasScheduledPublish ? (
									<HubTooltip
										title={
											canUploadGMBTemplates
												? template.scheduledPublishAt &&
													new Date(template.scheduledPublishAt).getTime() <=
														Date.now()
													? t('google-ads:scheduledPostAlreadyPublishing')
													: ''
												: uploadMissingPermissionText
										}
									>
										<ButtonWithLoading
											onClick={handleCancelSchedule}
											loading={cancelSchedulePending}
											color="warning"
											variant="outlined"
											disabled={
												!canUploadGMBTemplates ||
												!template.scheduledPublishAt ||
												new Date(template.scheduledPublishAt).getTime() <=
													Date.now()
											}
										>
											{t('google-ads:cancelScheduled')}
										</ButtonWithLoading>
									</HubTooltip>
								) : null}
								<HubTooltip
									title={
										canUploadGMBTemplates
											? watchedLocations?.length
												? ''
												: t('uploadNoLocations')
											: uploadMissingPermissionText
									}
								>
									<ButtonWithLoading
										onClick={handleUpload}
										loading={uploadPending}
										color="primary"
										variant="outlined"
										disabled={
											!canUploadGMBTemplates || !watchedLocations?.length
										}
									>
										{t('google-ads:publishNow')}
									</ButtonWithLoading>
								</HubTooltip>
							</div>
						)}
					</div>
					{template.id !== -1 && template.scheduledPublishAt ? (
						<div
							css={{
								paddingLeft: '1.5rem',
								paddingRight: '1.5rem',
								paddingBottom: '0.75rem',
								color: theme.colors.text.secondary,
							}}
						>
							{t('google-ads:scheduledFor', {
								dateTime: formatDateAndTime(template.scheduledPublishAt),
							})}
						</div>
					) : null}
					<ScrollShadowBox
						outerStyle={{
							flexShrink: '1',
							minHeight: '0px',
							height: '100%',
							width: `${pageWidth - (containerRect?.left ?? 0)}px`,
						}}
					>
						<div>
							<div
								css={{
									paddingLeft: '1.5rem',
									paddingRight: '1.5rem',
									display: section === 'template' ? undefined : 'none',
								}}
							>
								<div css={[styles.grid, { paddingLeft: 0 }]}>
									<InputTextFieldWithControl
										name="name"
										label={t('google-ads:name') + REQUIRED_MARK}
										placeholder={t('google-ads:name')}
										outerStyle={{ gridColumn: 'span 8 / span 8' }}
										readOnly={!canEditGMBTemplates}
									/>
									<GoogleAdsLocationSelector />
									<SelectorSelectWithControl
										name="postType"
										label={t('google-ads:type') + REQUIRED_MARK}
										placeholder={t('google-ads:type')}
										items={['STANDARD', 'OFFER', 'EVENT']}
										outerStyle={{ gridColumn: 'span 4 / span 4' }}
										readOnly={!canEditGMBTemplates}
									/>
									<SelectorSelectWithLookupAndControl
										name="languageCode"
										label={t('google-ads:language') + REQUIRED_MARK}
										placeholder={t('google-ads:language')}
										lookup={languageOptions.lookUp}
										outerStyle={{ gridColumn: 'span 4 / span 4' }}
										readOnly={!canEditGMBTemplates}
									/>
									{postType === 'STANDARD' ? null : (
										<InputTextFieldLengthWithControl
											name="title"
											label={t('google-ads:title') + REQUIRED_MARK}
											placeholder={t('google-ads:title')}
											outerStyle={{ gridColumn: 'span 8 / span 8' }}
											readOnly={!canEditGMBTemplates}
											maxLength={58}
										/>
									)}
									<TextareaWithControl
										name="summary"
										label={t('google-ads:summary') + REQUIRED_MARK}
										placeholder={t('google-ads:summary')}
										outerStyle={{ gridColumn: 'span 8 / span 8' }}
										helperText={t('google-ads:gmbWildcardInfo')}
										readOnly={!canEditGMBTemplates}
									/>
									{postType === 'STANDARD' ? (
										<>
											<SelectorSelectWithControl
												name="ctaType"
												label={t('google-ads:ctaType') + REQUIRED_MARK}
												placeholder={t('google-ads:ctaType')}
												items={[
													'BOOK',
													'ORDER',
													'SHOP',
													'LEARN_MORE',
													'SIGN_UP',
													'CALL',
												]}
												outerStyle={{ gridColumn: 'span 2 / span 2' }}
												readOnly={!canEditGMBTemplates}
											/>
											{GMB_STANDARD_CTA_TYPE_CTA_URL_NOT_NEEDED.includes(
												ctaType
											) ? null : (
												<InputTextFieldWithControl
													name="ctaUrl"
													label={t('google-ads:ctaUrl') + REQUIRED_MARK}
													placeholder={t('google-ads:ctaUrl')}
													outerStyle={{ gridColumn: 'span 6 / span 6' }}
													readOnly={!canEditGMBTemplates}
													infoText={t('facebook:redirectUrlInfo')}
												/>
											)}
										</>
									) : postType === 'EVENT' || postType === 'OFFER' ? (
										<>
											<SelectorDateTimeWithControl
												name="startDate"
												label={t('google-ads:eventStartDate') + REQUIRED_MARK}
												outerStyle={{ gridColumn: 'span 4 / span 4' }}
												readOnly={!canEditGMBTemplates}
												clearable
											/>
											<SelectorDateTimeWithControl
												name="endDate"
												label={t('google-ads:eventEndDate') + REQUIRED_MARK}
												outerStyle={{ gridColumn: 'span 4 / span 4' }}
												readOnly={!canEditGMBTemplates}
												clearable
											/>
											{postType === 'OFFER' ? (
												<>
													<InputTextFieldWithControl
														name="redeemOnlineUrl"
														label={
															t('google-ads:redeemOnlineUrl') + REQUIRED_MARK
														}
														placeholder={t('google-ads:redeemOnlineUrl')}
														outerStyle={{ gridColumn: 'span 8 / span 8' }}
														readOnly={!canEditGMBTemplates}
														infoText={t('facebook:redirectUrlInfo')}
													/>
													<InputTextFieldWithControl
														name="couponCode"
														label={t('google-ads:couponCode') + REQUIRED_MARK}
														placeholder={t('google-ads:couponCode')}
														outerStyle={{ gridColumn: 'span 4 / span 4' }}
														readOnly={!canEditGMBTemplates}
													/>
													<InputTextFieldWithControl
														name="termsConditions"
														label={
															t('google-ads:termsConditions') + REQUIRED_MARK
														}
														placeholder={t('google-ads:termsConditions')}
														outerStyle={{ gridColumn: 'span 4 / span 4' }}
														readOnly={!canEditGMBTemplates}
													/>
												</>
											) : null}
										</>
									) : null}
									{mediaFile ? (
										<div
											css={{
												display: 'flex',
												marginTop: '0.25rem',
												marginBottom: '0.25rem',
											}}
										>
											<HubTooltip title={mediaFile?.name}>
												<div>
													<RenderSimpleImage
														imageStyle={{
															height: 74,
															width: 74,
														}}
														src={mediaFile?.url}
													/>
												</div>
											</HubTooltip>
											<ButtonIconDelete
												onClick={() => formMethods.setValue('image', undefined)}
											/>
										</div>
									) : (
										<div
											css={[
												styles.add,
												{ gridColumn: 'span 8 / span 8', paddingLeft: 0 },
											]}
											onClick={() => setShowMedia(true)}
										>
											{t('facebook:addAdCreativeMedia')}
											<IconAddFilled css={{ width: '1em', height: '1em' }} />
										</div>
									)}
								</div>
							</div>
							<div
								css={{
									display: section === 'history' ? undefined : 'none',
								}}
							>
								<GMBTemplateHistory templateId={template.id} />
							</div>
						</div>
					</ScrollShadowBox>
					<div css={{ paddingLeft: '1.5rem', paddingRight: '1.5rem' }}>
						<ButtonRow
							left={
								<>
									{template.id === -1 ? null : (
										<>
											<HubTooltip title={deleteMissingPermissionText}>
												<ButtonWithLoading
													onClick={() => setConfirmDelete(true)}
													loading={deletePending}
													color="delete"
													variant="outlined"
													disabled={!canDeleteGMBTemplates}
												>
													{t('facebook:deleteFacebookTemplateSection.confirm')}
												</ButtonWithLoading>
											</HubTooltip>
											<GMBDuplicateTemplate templateId={template.id} />
										</>
									)}
								</>
							}
							right={
								<>
									<LinkButton to={goToGMBTemplatesList} color="primary">
										{t('common:buttons.cancel')}
									</LinkButton>

									<HubTooltip title={editMissingPermissionText}>
										<ButtonSave
											type="submit"
											onClick={() => setSection('template')}
											disabled={
												nameValidationPending ||
												(!!template.id &&
													!canEditGMBTemplates &&
													template.id !== -1)
											}
										/>
									</HubTooltip>
								</>
							}
						/>
					</div>
				</form>
				<WarnOnNavAway
					formState={formMethods.formState}
					resetForm={() => formMethods.reset()}
					ignoredSearchParams={['section']}
				/>
				<ModalDialog
					show={confirmDelete}
					onCancel={() => setConfirmDelete(false)}
					onConfirm={() => {
						deleteTemplate([template.id])
							.then(() => {
								navigate(goToGMBTemplatesList);
							})
							.catch(() => {
								setConfirmDelete(false);
							});
						setConfirmDelete(false);
					}}
					headerContent={t('facebook:deleteFacebookTemplateSection.title')}
					bodyContent={
						<Trans
							ns="facebook"
							i18nKey="deleteFacebookTemplateSection.description"
							values={{ title: 'template', name: template.name }}
							components={{ br: <br /> }}
						/>
					}
					confirmButtonContent={t(
						'facebook:deleteFacebookTemplateSection.confirm'
					)}
					type="archive"
				/>
				<TemplateMediaModal
					isOpen={showMedia}
					onClose={() => setShowMedia(false)}
					onSave={(selectedMedia) => {
						formMethods.setValue('image', selectedMedia[0] || undefined, {
							shouldDirty: true,
						});
					}}
					destination="GMB"
					maxSelections={1}
					selected={mediaFile ? [mediaFile] : []}
				/>
			</CollapseSectionProvider>
		</FormProvider>
	);
}
